// MeasurementNode.js - Measurement Node Component with Sweep Support

import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import styled from 'styled-components';
import Plot from '../Plots/PlotlyChart';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';

// Measurement symbol: oscilloscope/graph icon
const MEASUREMENT_SYMBOL = 'M10,25 L13,20 L16,15 L19,18 L22,12 L25,18 L28,15 L30,20';

const MeasurementNode = ({ data, id }) => {
  const { calculationResults, sweepResults, frequencyMode, isSweeping, showTutorial, completeTutorialAction } = useStore();

  const results = calculationResults[id];
  const isSweepMode = frequencyMode === 'sweep';
  const hasSweepData = sweepResults && sweepResults.frequencies && sweepResults.frequencies.length > 0;

  // Function to open the full plot panel
  const handleOpenPlotPanel = () => {
    // Trigger the event that MainLayout listens for
    window.dispatchEvent(new CustomEvent('open-plot-panel'));
    // Track for tutorial
    if (showTutorial) {
      completeTutorialAction('miniPlotClicked');
    }
  };

  // Render single-frequency mode
  const renderSingleMode = () => {
    if (!results) {
      return <EmptyMessage>No data</EmptyMessage>;
    }

    return (
      <>
        <MetricRow>
          <MetricLabel>Power:</MetricLabel>
          <MetricValue>{results.outputPower || 'N/A'} dBm</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>System Gain:</MetricLabel>
          <MetricValue>{results.cumulativeGain || 'N/A'} dB</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>NF:</MetricLabel>
          <MetricValue>{results.cumulativeNF || results.noiseFigure || 'N/A'} dB</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>OP1dB:</MetricLabel>
          <MetricValue>{results.p1db || 'N/A'} dBm</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>OIP3:</MetricLabel>
          <MetricValue>{results.cumulativeOIP3 || results.oip3 || 'N/A'} dBm</MetricValue>
        </MetricRow>
      </>
    );
  };

  // Render sweep mode with mini plots
  const renderSweepMode = () => {
    if (isSweeping) {
      return <EmptyMessage>⏳ Calculating...</EmptyMessage>;
    }

    if (!hasSweepData) {
      return <EmptyMessage>Waiting for sweep data</EmptyMessage>;
    }

    // Configuration for mini plots
    const miniPlotConfig = {
      displayModeBar: false,
      staticPlot: false,
      responsive: true
    };

    // Filter to only include frequency points where we have valid data (non-null gain)
    const validIndices = [];
    for (let i = 0; i < sweepResults.frequencies.length; i++) {
      if (sweepResults.gain[i] !== null) {
        validIndices.push(i);
      }
    }

    // Extract valid data subset
    const validFrequencies = validIndices.map(i => sweepResults.frequencies[i]);
    const validGain = validIndices.map(i => sweepResults.gain[i]);
    const validNF = sweepResults.noiseFigure ? validIndices.map(i => sweepResults.noiseFigure[i]) : null;
    const validP1dB = sweepResults.p1db ? validIndices.map(i => sweepResults.p1db[i]) : null;
    const validOIP3 = sweepResults.oip3 ? validIndices.map(i => sweepResults.oip3[i]) : null;

    // Determine actual data range for x-axis labels
    const hasValidData = validFrequencies.length > 0;
    const startFreq = hasValidData ? Math.round(validFrequencies[0]) : Math.round(sweepResults.frequencies[0]);
    const stopFreq = hasValidData ? Math.round(validFrequencies[validFrequencies.length - 1]) : Math.round(sweepResults.frequencies[sweepResults.frequencies.length - 1]);

    const commonLayout = {
      height: 60,
      margin: { t: 5, r: 5, b: 16, l: 28 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      font: { size: 9, color: THEME.textPrimary },
      xaxis: {
        showgrid: false,
        showticklabels: false,
        zeroline: false,
        color: THEME.textSecondary
      },
      yaxis: {
        showgrid: true,
        gridcolor: THEME.gridDots,
        showticklabels: true,
        zeroline: false,
        color: THEME.textSecondary,
        tickfont: { size: 8 }
      },
      annotations: [
        {
          x: startFreq,
          y: 0,
          xref: 'x',
          yref: 'paper',
          text: `${startFreq} MHz`,
          showarrow: false,
          font: { size: 7, color: THEME.textSecondary },
          xanchor: 'left',
          yanchor: 'top',
          yshift: -2
        },
        {
          x: stopFreq,
          y: 0,
          xref: 'x',
          yref: 'paper',
          text: `${stopFreq} MHz`,
          showarrow: false,
          font: { size: 7, color: THEME.textSecondary },
          xanchor: 'right',
          yanchor: 'top',
          yshift: -2
        }
      ]
    };

    const createMiniPlot = (yData, label, color) => {
      return (
        <MiniPlotContainer key={label}>
          <PlotLabel>{label}</PlotLabel>
          <Plot
            data={[{
              x: validFrequencies,
              y: yData,
              type: 'scatter',
              mode: 'lines',
              line: { color: color, width: 1.5 },
              hovertemplate: '%{y:.2f}<extra></extra>'
            }]}
            layout={commonLayout}
            config={miniPlotConfig}
            style={{ width: '100%', height: '60px' }}
          />
        </MiniPlotContainer>
      );
    };

    if (!hasValidData) {
      return <EmptyMessage>No valid data in sweep range</EmptyMessage>;
    }

    return (
      <>
        {validGain.length > 0 && createMiniPlot(validGain, 'Gain (dB)', THEME.accentPrimary)}
        {validNF && validNF.length > 0 && createMiniPlot(validNF, 'NF (dB)', THEME.info)}
        {validP1dB && validP1dB.length > 0 && createMiniPlot(validP1dB, 'OP1dB (dBm)', '#F59E0B')}
        {validOIP3 && validOIP3.length > 0 && createMiniPlot(validOIP3, 'OIP3 (dBm)', '#8B5CF6')}

        <ViewDetailsLink onClick={handleOpenPlotPanel}>
          View Details →
        </ViewDetailsLink>
      </>
    );
  };

  return (
    <NodeContainer>
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="rfIn"
        style={{
          top: '60px',
          background: '#3b82f6',
          border: `2px solid ${THEME.textPrimary}`
        }}
      />

      {/* Card Content */}
      <Header className="drag-handle">
        <HeaderText>🔬 MEASUREMENT</HeaderText>
      </Header>

      <Body isSweepMode={isSweepMode}>
        {/* Measurement Symbol Watermark */}
        <SymbolWatermark viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <path d={MEASUREMENT_SYMBOL} />
        </SymbolWatermark>

        {isSweepMode ? renderSweepMode() : renderSingleMode()}
      </Body>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="rfOut"
        style={{
          top: '60px',
          background: '#3b82f6',
          border: `2px solid ${THEME.textPrimary}`
        }}
      />
    </NodeContainer>
  );
};

// Styled Components
const NodeContainer = styled.div`
  background: ${THEME.cardBackground};
  border: 2px solid ${THEME.cardBorder};
  border-radius: ${THEME.borderRadiusMedium};
  min-width: 220px;
  box-shadow: ${THEME.shadowMedium};
  font-family: 'Courier New', monospace;
  transition: all ${THEME.transitionFast};

  &:hover {
    box-shadow: ${THEME.shadowLarge};
  }

  .react-flow__node.selected & {
    border-color: ${THEME.accentPrimary};
    box-shadow: 0 0 0 2px ${THEME.accentPrimary};
  }
`;

const Header = styled.div`
  background: #1e3a8a;
  padding: 8px 12px;
  border-radius: ${THEME.borderRadiusMedium} ${THEME.borderRadiusMedium} 0 0;
  text-align: center;
  font-weight: bold;
  font-size: 13px;
  letter-spacing: 0.5px;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const HeaderText = styled.div`
  color: #fff;
  text-transform: uppercase;
`;

const Body = styled.div`
  padding: 12px;
  position: relative;
  min-height: ${props => props.isSweepMode ? '320px' : '150px'};
  background: ${THEME.cardBackground};
  cursor: default;
`;

const SymbolWatermark = styled.svg`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 160px;
  height: 160px;
  opacity: 0.15;
  pointer-events: none;

  path {
    fill: none;
    stroke: #3b82f6;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid ${THEME.inputBorder};
  position: relative;
  z-index: 1;

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled.span`
  font-size: 12px;
  color: ${THEME.textSecondary};
  font-weight: 600;
`;

const MetricValue = styled.span`
  font-size: 13px;
  color: ${THEME.textPrimary};
  font-weight: bold;
  font-family: 'Courier New', monospace;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: ${THEME.textSecondary};
  font-size: 12px;
  padding: 20px;
  position: relative;
  z-index: 1;
`;

const MiniPlotContainer = styled.div`
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

const PlotLabel = styled.div`
  font-size: 10px;
  color: ${THEME.textPrimary};
  font-weight: 600;
  margin-bottom: 2px;
`;

const ViewDetailsLink = styled.button`
  width: 100%;
  padding: 4px;
  margin-top: 0;
  background: transparent;
  color: ${THEME.textMuted};
  border: none;
  font-size: 10px;
  cursor: pointer;
  transition: color 0.2s;
  position: relative;
  z-index: 1;
  text-align: center;

  &:hover {
    color: ${THEME.textPrimary};
  }
`;

export default memo(MeasurementNode);
